// Sidebar redesign — density toggle hook + provider.
//
// Persists `full | rail` to localStorage so a reload restores the user's
// last choice. Same-tab consumers share state via a provider (the layout
// and the sidebar both read it); cross-tab updates fall back to the
// `storage` event so multiple Cold Caller windows stay in sync.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type SidebarDensity = 'full' | 'rail';

export const SIDEBAR_DENSITY_STORAGE_KEY = 'coldcaller:sidebar-density';

function readDensity(): SidebarDensity {
  if (typeof window === 'undefined') return 'full';
  try {
    const v = window.localStorage.getItem(SIDEBAR_DENSITY_STORAGE_KEY);
    return v === 'rail' ? 'rail' : 'full';
  } catch {
    return 'full';
  }
}

function writeDensity(d: SidebarDensity): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SIDEBAR_DENSITY_STORAGE_KEY, d);
  } catch {
    /* noop — localStorage disabled / full */
  }
}

export interface SidebarDensityContextValue {
  density: SidebarDensity;
  setDensity: (d: SidebarDensity) => void;
  toggleDensity: () => void;
}

const SidebarDensityContext = createContext<SidebarDensityContextValue | null>(null);

export const SidebarDensityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [density, setDensityState] = useState<SidebarDensity>(readDensity);

  const setDensity = useCallback((d: SidebarDensity) => {
    setDensityState(d);
    writeDensity(d);
  }, []);

  const toggleDensity = useCallback(() => {
    setDensityState((prev) => {
      const next: SidebarDensity = prev === 'rail' ? 'full' : 'rail';
      writeDensity(next);
      return next;
    });
  }, []);

  // Cross-tab sync — `storage` only fires in OTHER documents, but covers
  // the multi-tab use case (operator with two windows open).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SIDEBAR_DENSITY_STORAGE_KEY) return;
      setDensityState(e.newValue === 'rail' ? 'rail' : 'full');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<SidebarDensityContextValue>(
    () => ({ density, setDensity, toggleDensity }),
    [density, setDensity, toggleDensity],
  );

  return (
    <SidebarDensityContext.Provider value={value}>{children}</SidebarDensityContext.Provider>
  );
};

export function useSidebarDensity(): SidebarDensityContextValue {
  const ctx = useContext(SidebarDensityContext);

  // Always run the fallback hooks so hooks-order is stable across
  // renders — they're cheap, and only the return value differs based on
  // whether a provider is mounted. This keeps the Sidebar usable in
  // isolation (unit tests, future "preview" surfaces) while still
  // sharing state with the layout when the provider is present.
  const [localDensity, setLocalDensity] = useState<SidebarDensity>(readDensity);
  const setLocal = useCallback((d: SidebarDensity) => {
    setLocalDensity(d);
    writeDensity(d);
  }, []);
  const toggleLocal = useCallback(() => {
    setLocalDensity((prev) => {
      const next: SidebarDensity = prev === 'rail' ? 'full' : 'rail';
      writeDensity(next);
      return next;
    });
  }, []);

  if (ctx) return ctx;
  return { density: localDensity, setDensity: setLocal, toggleDensity: toggleLocal };
}
